import * as v from 'valibot';
import { PresetPlaceSchema } from '~offerFlow/schemas/ProductConfigPreset';
import { ROUTE_VARIANT_VALUES } from '~/core/types/RouteVariant';

function createProductPresetDateSchema(description: string, examples = ['2026-08-05']) {
    return v.pipe(
        v.string(),
        v.regex(/^\d{4}-\d{2}-\d{2}$/),
        v.description(description),
        v.examples(examples),
        v.transform((val: string) => zurichDay(val)),
    );
}

const ProductPresetDateSchema = createProductPresetDateSchema(
    'Travel date in YYYY-MM-DD format. The date is interpreted in the Europe/Zurich time zone.',
);

const ProductPresetDateUntilSchema = createProductPresetDateSchema(
    'End date in YYYY-MM-DD format. For multi-day products this is the final NOT-included travel day.',
    ['2026-08-12'],
);

// Price object, e.g. { currency: "CHF", amount: 14060 }
export const PresetPriceSchema = v.pipe(
    v.object({
        currency: v.pipe(
            v.string(),
            v.maxLength(3),
            v.description('Currency code for the displayed starting price.'),
            v.examples(['CHF']),
        ),
        amount: v.pipe(
            v.number(),
            v.integer(),
            v.description('Starting-price amount in cents/Rappen for CHF.'),
            v.examples([12300]),
        ),
        vats: v.optional(v.pipe(
            v.array(v.unknown()),
            v.description('VAT details according to TofT API specs.'),
        )),
    }),
    v.title('Starting price'),
    v.description('Informational starting price.'),
);

export const ProductPresetSchemaP2P = v.pipe(
    v.object({
        dateFrom: v.optional(ProductPresetDateSchema),
        routeVariantUp: v.optional(v.pipe(
            v.picklist(ROUTE_VARIANT_VALUES),
            v.description('Preferred route variant for the outbound journey.'),
        )),
        routeVariantDown: v.optional(v.pipe(
            v.picklist(ROUTE_VARIANT_VALUES),
            v.description('Preferred route variant for the return journey.'),
        )),
        origin: v.optional(v.pipe(
            PresetPlaceSchema,
            v.description('Preselected departure place as a UIC station URN.'),
            v.examples(['urn:uic:stn:8505226']),
        )),
        startingPrice: v.optional(PresetPriceSchema),
        tripUpId: v.optional(v.pipe(
            v.string(),
            v.description('Identifier of the preselected outbound trip.'),
        )),
        tripDownId: v.optional(v.pipe(
            v.string(),
            v.description('Identifier of the preselected return trip.'),
        )),
    }),
    v.title('P2P product preset'),
    v.description('Prefill parameters supported by P2P booking products.'),
);

export const ProductPresetSchemaActivity = v.pipe(
    v.object({
        dateFrom: v.optional(ProductPresetDateSchema),
        dateUntil: v.optional(ProductPresetDateUntilSchema),
        startingPrice: v.optional(PresetPriceSchema),
    }),
    v.title('Activity product preset'),
    v.description('Prefill parameters supported by activity booking products.'),
);

export type ProductPresetSchema = typeof ProductPresetSchemaP2P | typeof ProductPresetSchemaActivity;
